<template>
    <div class="substance-requirement-summary">
        <div class="substance-requirement-summary__main">
            <div
                class="substance-requirement-summary__identity"
                :class="{
                    'substance-requirement-summary__identity--clickable': canToggleEditor,
                }"
                :role="canToggleEditor ? 'button' : null"
                :tabindex="canToggleEditor ? 0 : null"
                :aria-expanded="canToggleEditor ? (showEditor ? 'true' : 'false') : null"
                @click="toggleEditor"
                @keydown.enter.prevent="toggleEditor"
                @keydown.space.prevent="toggleEditor"
            >
                <h6 class="mb-50">{{ identity.title }}</h6>
                <div class="substance-requirement-summary__identifiers">
                    <span v-if="hasCas">CAS {{ identity.cas }}</span>
                    <span v-if="hasEc">EC {{ identity.ec }}</span>
                    <span v-if="placement.componentPath">{{ placement.componentPath }}</span>
                </div>
            </div>
            <div
                v-if="editable"
                class="substance-requirement-summary__actions"
            >
                <BBadge
                    v-if="showRequirementStatus && informationRequired"
                    variant="light-warning"
                    class="substance-requirement-summary__requirement-status"
                >
                    {{ translate('Component(Status):information-required', 'Information required') }}
                </BBadge>
                <WorkflowSaveStatus
                    :step="localSaveStep"
                    :show-text="false"
                    class="substance-requirement-summary__save-status"
                />
                <b-button
                    v-if="hasEditableProperties"
                    class="substance-requirement-summary__edit"
                    variant="outline-primary"
                    size="sm"
                    :aria-expanded="showEditor ? 'true' : 'false'"
                    @click="toggleEditor"
                >
                    {{ showEditor
                        ? translate('Component(Button):close', 'Close')
                        : translate('Component(Button):edit', 'Edit') }}
                </b-button>
                <b-button
                    variant="outline-danger"
                    class="btn-icon"
                    size="sm"
                    :title="translate('Component(Button):remove', 'Remove')"
                    :aria-label="translate('Component(Button):remove', 'Remove')"
                    :disabled="hasLocalSaveActivity"
                    @click="exclude"
                >
                    <feather-icon icon="Trash2Icon" />
                </b-button>
            </div>
        </div>

        <div
            v-if="!showEditor && summaryProperties.length > 0"
            class="substance-requirement-summary__properties"
        >
            <div
                v-for="property in summaryProperties"
                :key="property.type"
                class="substance-requirement-summary__property"
            >
                <span>{{ property.label }}:</span>
                <LimitedBadgeList
                    v-if="property.isLimitedList"
                    :items="property.rawValue"
                    :item-label="formatValue"
                    :limit="3"
                />
                <strong v-else>{{ property.value }}</strong>
            </div>
        </div>

        <div
            v-if="!showEditor && exemptionProperty"
            class="substance-requirement-summary__exemption"
        >
            <span>{{ exemptionProperty.label }}:</span>
            <strong>{{ exemptionProperty.value }}</strong>
        </div>

        <div
            v-if="showEditor"
            class="substance-requirement-summary__editor"
        >
            <SubstanceListPropertiesEditor
                :read-only="false"
                :substance="placement.container"
                :substance-list="list"
                :property-config="propertyConfig"
                :hide-candidate-list-categories="shouldHideCandidateListCategories"
                layout="grid"
                @change="changeProperties"
            />
        </div>
    </div>
</template>

<script>
import { BBadge, BButton } from 'bootstrap-vue'
import { ProductPropertyType } from '@core/domain/substance'
import { SubstanceLists } from '@/libs/substance'
import LimitedBadgeList from '@core/components/common/LimitedBadgeList.vue'
import SubstanceListPropertiesEditor from '@/views/product/components/product-card/composition/substance-editor/SubstanceListPropertiesEditor.vue'
import WorkflowSaveStatus from '@/views/request/received/workflow/components/WorkflowSaveStatus.vue'
import { listPropertyDefinitions, propertyValue } from '../compliance-requirements'

export default {
    name: 'SubstanceRequirementSummary',
    components: {
        BBadge,
        BButton,
        LimitedBadgeList,
        SubstanceListPropertiesEditor,
        WorkflowSaveStatus,
    },
    props: {
        placement: { type: Object, required: true },
        list: { type: Object, required: true },
        propertyConfig: { type: Object, required: true },
        editable: { type: Boolean, default: true },
        editorExpanded: { type: Boolean, default: false },
        hideCandidateListCategories: { type: Boolean, default: false },
        informationRequired: { type: Boolean, default: false },
        showRequirementStatus: { type: Boolean, default: true },
    },
    data() {
        return {
            pendingSaveOperations: 0,
            activeSaveOperations: 0,
            localSaveError: null,
        }
    },
    computed: {
        identity() {
            return this.placement.container.substance || {}
        },
        hasCas() {
            return this.identity.cas && this.identity.cas !== '-'
        },
        hasEc() {
            return this.identity.ec && this.identity.ec !== '-'
        },
        isLocallySaving() {
            return this.activeSaveOperations > 0
        },
        hasLocalSaveActivity() {
            return this.pendingSaveOperations > 0 || this.isLocallySaving
        },
        hasEditableProperties() {
            if (this.list.key === SubstanceLists.CANDIDATE_LIST) {
                return true
            }

            return listPropertyDefinitions(this.list, this.propertyConfig)
                .some(property => Boolean(property.name))
        },
        shouldHideCandidateListCategories() {
            return this.hideCandidateListCategories
                && this.list.key === SubstanceLists.CANDIDATE_LIST
        },
        showEditor() {
            return this.editorExpanded && this.hasEditableProperties
        },
        canToggleEditor() {
            return this.editable && this.hasEditableProperties
        },
        localSaveStep() {
            return {
                id: `substance-${this.identity.id}-${this.list.id}`,
                isSaving: this.isLocallySaving,
                isDirty: this.pendingSaveOperations > 0,
                error: this.hasLocalSaveActivity ? null : this.localSaveError,
            }
        },
        renderedProperties() {
            return listPropertyDefinitions(this.list, this.propertyConfig)
                .map(property => {
                    const rawValue = propertyValue(
                        this.placement.container,
                        property,
                        this.list.id,
                    )

                    return {
                        type: property.type,
                        label: this.propertyLabel(property),
                        rawValue,
                        value: this.formatValue(rawValue),
                        isLimitedList: this.isLimitedListProperty(property.type, rawValue),
                    }
                })
                .filter(property => property.value !== '')
        },
        exemptionProperty() {
            return this.renderedProperties.find(property => property.type === ProductPropertyType.EXEMPTION) || null
        },
        summaryProperties() {
            const hiddenTypes = this.shouldHideCandidateListCategories
                ? [
                    ProductPropertyType.MATERIAL_CATEGORY,
                    ProductPropertyType.MIXTURE_CATEGORY,
                    ProductPropertyType.ADDITIONAL_MATERIAL,
                ]
                : []

            return this.renderedProperties.filter(property => (
                property.type !== ProductPropertyType.EXEMPTION
                && !hiddenTypes.includes(property.type)
            ))
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        toggleEditor() {
            if (this.canToggleEditor) {
                this.$emit('toggle-editor')
            }
        },
        propertyLabel(property) {
            const configuredLabel = property.properties && property.properties.label
            if (configuredLabel) {
                return this.translate(configuredLabel, property.type)
            }

            const labels = {
                [ProductPropertyType.MAX_CONCENTRATION]: 'Maximum concentration',
                [ProductPropertyType.MAX_CONCENTRATION_RANGE]: 'SCIP interval',
                [ProductPropertyType.MATERIAL_CATEGORY]: 'Material category',
                [ProductPropertyType.MIXTURE_CATEGORY]: 'Mixture category',
                [ProductPropertyType.ADDITIONAL_MATERIAL]: 'Additional material characteristics',
                [ProductPropertyType.EXEMPTION]: 'Applicable exemption',
            }
            return labels[property.type] || property.type
        },
        isLimitedListProperty(type, value) {
            return Array.isArray(value) && [
                ProductPropertyType.MATERIAL_CATEGORY,
                ProductPropertyType.MIXTURE_CATEGORY,
                ProductPropertyType.ADDITIONAL_MATERIAL,
            ].includes(type)
        },
        formatValue(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return ''
            }
            if (Array.isArray(value)) {
                return value.map(this.formatValue).filter(Boolean).join(', ')
            }
            if (typeof value === 'object') {
                if (value.value !== undefined) {
                    return `${value.value}${value.unit ? ` ${value.unit}` : ''}`
                }
                return value.title || value.name || value.label || ''
            }
            return String(value)
        },
        beginSaveOperation() {
            this.pendingSaveOperations += 1
            this.localSaveError = null
            let settled = false
            let saving = false

            const settle = error => {
                if (settled || this._isDestroyed) {
                    return
                }
                settled = true
                if (saving) {
                    this.activeSaveOperations = Math.max(0, this.activeSaveOperations - 1)
                } else {
                    this.pendingSaveOperations = Math.max(0, this.pendingSaveOperations - 1)
                }
                if (error) {
                    this.localSaveError = error
                }
            }

            return {
                savingCallback: () => {
                    if (settled || saving || this._isDestroyed) {
                        return
                    }
                    saving = true
                    this.pendingSaveOperations = Math.max(0, this.pendingSaveOperations - 1)
                    this.activeSaveOperations += 1
                },
                completionCallback: () => settle(null),
                failureCallback: error => settle(error),
            }
        },
        changeProperties(substance, properties) {
            const callbacks = this.beginSaveOperation()
            this.$emit('change', substance, properties, callbacks)
        },
        exclude() {
            if (this.hasLocalSaveActivity) {
                return
            }
            const callbacks = this.beginSaveOperation()
            this.$emit('exclude', this.identity, callbacks)
        },
    },
}
</script>

<style lang="scss" scoped>
.substance-requirement-summary {
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    padding: 1rem;

    &__main {
        align-items: flex-start;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    &__identity {
        flex: 1 1 auto;
        min-width: 0;

        &--clickable {
            border-radius: .357rem;
            cursor: pointer;

            &:focus {
                outline: 0;
            }

            &:focus-visible {
                box-shadow: 0 0 0 .2rem rgba(115, 103, 240, .2);
            }
        }
    }

    &__actions {
        align-items: center;
        display: flex;
        flex: 0 0 auto;
        gap: .5rem;
    }

    &__save-status {
        margin-left: 0 !important;
    }

    &__identifiers {
        color: #6e6b7b;
        display: flex;
        flex-wrap: wrap;
        font-size: .875rem;
        gap: .25rem 1rem;
    }

    &__properties {
        display: flex;
        flex-wrap: wrap;
        gap: .5rem 1.5rem;
        margin-top: .75rem;
    }

    &__property,
    &__exemption {
        display: flex;
        gap: .35rem;
    }

    &__exemption {
        border-top: 1px solid #ebe9f1;
        margin-top: .75rem;
        padding-top: .75rem;
    }

    &__editor {
        border-top: 1px solid #ebe9f1;
        margin-top: 1rem;
        padding-top: 1rem;
    }
}
</style>
